import { useHostLauncherHidden } from "../apps/hostUI";
import { AssistantConversation } from "./AssistantConversation";
import { helperDraftRequest, helperWelcomeSettings, type HelperDraftRequest } from "./conversationComposer";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useProjects } from "../../hooks/useProjects";
import { ASSISTANT_CHAT_SLOT, useAssistantDirectory, useAssistantPreferences } from "../../hooks/useChatAssistant";
import { useRealtimeVoice } from "../../state/RealtimeVoiceContext";
import { instances, platformHelper } from "../../api";
import { AgentMark } from "../AgentMark";
import { ChatAgentPicker } from "./ChatAgentPicker";
import { useAssistantPageContext } from "./pageContext";
import { allowedAssistantChoices, initialAssistantTarget, targetKey } from "./assistantModel";
import type { WidgetContext } from "../apps/widgetContext";

export function ChatAssistantDock() {
  const { currentProject, projects } = useProjects();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const workspace = location.pathname === "/" || location.pathname.startsWith("/pages/") || (location.pathname === "/settings" && params.get("tab") === "pages");
  const requested = workspace ? projects.find(project => project.id === params.get("project")) : undefined;
  const projectId = requested?.id || currentProject?.id || "";
  const [helperSetupError, setHelperSetupError] = useState("");
  useEffect(() => {
    let cancelled = false;
    let pending = false;
    const ensure = async () => {
      if (pending) return;
      pending = true;
      try {
        await platformHelper.ensureDefault();
        if (!cancelled) {
          setHelperSetupError("");
          window.dispatchEvent(new Event("apteva:helper-changed"));
        }
      } catch (reason) {
        if (!cancelled) setHelperSetupError(reason instanceof Error ? reason.message : "Unable to prepare Helper.");
      } finally { pending = false; }
    };
    const refresh = () => { void ensure(); };
    void ensure();
    for (const event of ["apteva:apps-changed", "apteva:connections-changed", "focus"]) window.addEventListener(event, refresh);
    return () => {
      cancelled = true;
      for (const event of ["apteva:apps-changed", "apteva:connections-changed", "focus"]) window.removeEventListener(event, refresh);
    };
  }, [projectId, location.pathname]);
  return <ProjectChatAssistant key={projectId} projectId={projectId} helperSetupError={helperSetupError} />;
}
function ProjectChatAssistant({ projectId, helperSetupError }: { projectId: string; helperSetupError: string }) {
  const launcherHidden = useHostLauncherHidden(projectId);
  const { currentProject } = useProjects();
  const pageContext = useAssistantPageContext(projectId, currentProject?.name);
  const { preferences, save } = useAssistantPreferences(projectId);
  const { session: voiceSession } = useRealtimeVoice();
  const { directory, loading, error, refresh } = useAssistantDirectory(projectId, preferences.enabled);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const [draftRequest, setDraftRequest] = useState<HelperDraftRequest>();
  useEffect(() => { if (!open) setDraftRequest(undefined); }, [open]);
  const panel = useRef<HTMLDialogElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const choices = allowedAssistantChoices(preferences, directory?.choices || []);
  const activeKey = selected || initialAssistantTarget(preferences, directory?.choices || []);
  useEffect(() => { setStartError(""); }, [activeKey]);
  const choice = choices.find((item) => targetKey(item.target) === activeKey);
  const helperDisabled = preferences.defaultTarget.kind === "helper" && directory?.helper?.default_enabled === false && choices.length === 0;
  const available = preferences.enabled && !!directory?.contribution && !helperDisabled;
  useEffect(() => {
    const ask = (event: Event) => {
      const detail = (event as CustomEvent<{ prompt?: string; context?: WidgetContext; handled?: boolean; inline?: boolean }>).detail;
      if (!detail || detail.handled || detail.inline || !available || detail.context?.projectId !== projectId) return;
      const helper = choices.find(item => item.target.kind === "helper");
      if (!helper) return;
      detail.handled = true;
      setSelected(targetKey(helper.target));
      setDraftRequest(helperDraftRequest(detail.prompt, projectId, helper.agent.id));
      setOpen(true);
    };
    window.addEventListener("apteva:helper-prompt", ask);
    return () => window.removeEventListener("apteva:helper-prompt", ask);
  }, [available, choices, projectId]);
  useEffect(() => {
    const show = (event: Event) => { if (available) { event.preventDefault(); setOpen(true); refresh(); } };
    window.addEventListener("apteva:assistant-open", show);
    return () => window.removeEventListener("apteva:assistant-open", show);
  }, [available, refresh]);
  // A preference change must not leave a previously allowed target mounted.
  const configuration = JSON.stringify([preferences.defaultTarget, preferences.allowSwitching, preferences.targets]);
  useEffect(() => { setSelected(null); }, [configuration]);
  useEffect(() => { if (!available) setOpen(false); }, [available]);
  useEffect(() => {
    if (open && available) { panel.current?.show(); panel.current?.focus(); }
    else panel.current?.close();
    return () => { panel.current?.close(); };
  }, [open, available]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  if (!available) return null;
  return <>
    {!launcherHidden && <button ref={launcher} type="button" onClick={() => { setSelected(null); refresh(); setOpen(true); }} className="floating-chat-launcher-safe touch-target fixed z-40 flex h-12 w-12 items-center justify-center rounded-full border border-accent/50 bg-accent text-bg shadow-xl hover:bg-accent-hover" title="Chat assistant" aria-label="Open chat assistant" aria-haspopup="dialog">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" /></svg>
    </button>}
    <dialog ref={panel} tabIndex={-1} aria-label="Chat assistant" onCancel={() => setOpen(false)} onClose={() => { setOpen(false); launcher.current?.focus(); }} className={`fixed inset-auto right-0 z-50 m-0 w-full max-w-full overflow-hidden rounded-none border border-border bg-bg p-0 text-text shadow-2xl sm:right-4 sm:w-[520px] sm:max-w-[calc(100vw-2rem)] sm:rounded-xl ${voiceSession ? "bottom-40 h-[min(620px,calc(100dvh-12rem))]" : "bottom-0 h-[100dvh] max-h-[100dvh] sm:bottom-4 sm:h-[min(720px,85dvh)]"}`}>
      {open && <div className="flex h-full min-h-0 flex-col">
        <header className="flex min-h-14 shrink-0 items-center gap-2 border-b border-border px-3">
          {preferences.allowSwitching && (choices.length > 1 || (!choice && choices.length > 0)) ? <ChatAgentPicker choices={choices} activeKey={activeKey} onSelect={(next) => {
            setDraftRequest(undefined);
            setSelected(targetKey(next.target));
            if (preferences.rememberTarget) void save({ ...preferences, lastTarget: next.target });
          }} /> : <>
            <AgentMark icon={choice?.agent.icon} color={choice?.agent.icon_color} size="sm" />
            <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{choice?.agent.name || "Chat assistant"}</h2>
          </>}
          <Link to="/settings?tab=chat-assistant" onClick={() => setOpen(false)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded text-text-muted hover:text-text" aria-label="Chat assistant settings" title="Chat assistant settings">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="M4 7h16M4 17h16M9 4v6M15 14v6" /></svg>
          </Link>
          <button type="button" onClick={() => setOpen(false)} className="h-10 w-10 shrink-0 rounded text-xl text-text-muted hover:bg-bg-hover" aria-label="Close chat assistant">×</button>
        </header>
        {(error || helperSetupError) && <p role="alert" className="p-3 text-sm text-red">{error || helperSetupError}</p>}
        {choice?.agent.status && choice.agent.status !== "running" && <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 text-xs text-text-muted">
          <span className="flex-1">Agent {choice.agent.status}</span>
          <button type="button" disabled={starting} onClick={async () => {
            setStarting(true); setStartError("");
            try { await instances.start(choice.agent.id); refresh(); }
            catch (reason) { setStartError(reason instanceof Error ? reason.message : "Could not start agent."); }
            finally { setStarting(false); }
          }} className="rounded border border-accent px-3 py-1.5 text-accent disabled:opacity-40">{starting ? "Starting…" : "Start agent"}</button>
          {startError && <span role="alert" className="w-full text-red">{startError}</span>}
        </div>}
        {choice && directory?.contribution ? <div className="min-h-0 flex-1 overflow-hidden"><AssistantConversation key={`${projectId}:${choice.agent.id}`} draftRequest={draftRequest} onDismissDraft={() => setDraftRequest(undefined)} projectId={projectId} agentId={choice.agent.id}  pageContext={preferences.sharePageContext ? pageContext : undefined} slot={ASSISTANT_CHAT_SLOT} apps={directory.rows} instance={{ id: `chat-assistant:${choice.agent.id}`, component: directory.contribution.key, contribution: directory.contribution, size: "full", settings: { ...(choice.target.kind === "helper" ? helperWelcomeSettings : {}), experience: "personal", display_mode: "single", composer_layout: "compact", show_new_conversation: true, show_page_context: false } }} /></div>
          : <div className="space-y-3 p-5 text-sm text-text-muted">{loading ? <p>Checking agent availability…</p> : directory?.helper && !directory.helper.provider_configured && preferences.defaultTarget.kind === "helper" ? <><p>Connect an AI provider to start chatting with Apteva Helper.</p><Link to="/settings?tab=providers" onClick={() => setOpen(false)} className="inline-flex rounded-lg bg-accent px-4 py-2 font-semibold text-bg">Connect AI</Link></> : <><p>This agent is unavailable. Choose another agent or update its settings.</p><Link to="/settings?tab=helper" onClick={() => setOpen(false)} className="text-accent">Helper settings →</Link></>}</div>}
      </div>}
    </dialog>
  </>;
}
